import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateBuckets } from 'creators/comments';

import { selectVideoComments } from 'selectors/comments';
import { selectCommentsEnabled, selectEmojiEnabled } from 'selectors/video';
import {
  selectAnimateReactions,
  selectLocalReactions,
  selectPlayerTime,
  selectVideoDuration,
  selectVideoReactions,
} from 'selectors/videoPlayer';

import CommentTimeline from '../../control-bar/comment-timeline';
import EmojiTimeline from '../../control-bar/seek-bar/emoji-timeline';

import styles from './styles.module.less';

const has = users => (users != null ? users.length !== 0 : false);

const commentCreatedByOneOf = users => comment => {
  if (!has(users)) {
    return true;
  }

  const userIds = users.map(u => u.id);

  return (
    userIds.includes(String(comment.user_id)) ||
    userIds.includes(comment.anon_user_id) ||
    (comment.replies &&
      comment.replies.filter(commentCreatedByOneOf(users)).length > 0)
  );
};

const reactionCreatedByOneOf = users => reaction => {
  if (!has(users)) {
    return true;
  }

  const userIds = users.map(u => u.id);

  return (
    userIds.includes(String(reaction.userId)) ||
    userIds.includes(reaction.anonId)
  );
};

const ReactionsTimeline = ({
  filter = { users: [] },
  suppressActiveBubble = false,
}) => {
  const dispatch = useDispatch();
  const reactions = useSelector(selectVideoReactions);
  const comments = useSelector(selectVideoComments);

  const { users } = filter;

  useEffect(() => {
    if (has(users)) {
      const filteredUserComments = comments.filter(
        commentCreatedByOneOf(users)
      );

      dispatch(updateBuckets(filteredUserComments));
    }

    return () => {
      if (has(users)) {
        dispatch(updateBuckets(comments));
      }
    };
  }, [comments, dispatch, users]);

  const localReactions = useSelector(selectLocalReactions);
  const duration = useSelector(selectVideoDuration);
  const currentTime = useSelector(selectPlayerTime);
  const animateReactions = useSelector(selectAnimateReactions);

  // TODO: Vlad: since duration starts as an int, we can maybe use audioDuration to prevent the jump of timeline elements.
  // const audioDuration = useSelector(selectAudioDuration);
  // const durationToUse = audioDuration || duration;

  const commentsEnabled = useSelector(selectCommentsEnabled);
  const emojiEnabled = useSelector(selectEmojiEnabled);

  const containerRef = useRef(null);

  const containerDimensions = {
    width: containerRef.current?.width || 0,
    x: 0,
  };

  return (
    <div className={styles.timelineContainer} ref={containerRef}>
      {commentsEnabled && (
        <CommentTimeline
          timelineIconClass={styles.reaction}
          duration={duration}
          suppressActiveBubble={suppressActiveBubble}
        />
      )}

      {emojiEnabled && (
        <div className={styles.emojiContainer}>
          <EmojiTimeline
            timelineIconClass={styles.reaction}
            reactions={reactions.filter(reactionCreatedByOneOf(users))}
            localReactions={localReactions}
            controlBarExpanded={true}
            containerDimensions={containerDimensions}
            animateReactions={animateReactions}
            currentTime={currentTime}
            duration={duration}
          />
        </div>
      )}
    </div>
  );
};

export default ReactionsTimeline;
